import {
  WorkflowJobState,
  RootState,
  WorkflowJobModel,
  WorkflowJobSearchModel,
  FieldUpdateModel,
  WorkflowJobTabModel,
  JobTabOrientation,
  JobTabStateModel,
  LockModel,
  ErrorModel,
  TabStateModel,
  DynamicFormValidationModel,
  WorkflowJobMutationTypes,
  CustomTabDynamicFormUpdateModel,
  WorkflowDocumentSearchModel,
  WorkflowFieldUpdateRuleModel,
  WorkflowRuleFieldModel,
  StoreConstants
} from '@/types';
import { ActionTree } from 'vuex';
import { WorkflowJobService } from '@/services/WorkflowJobService';
import { LockService } from '@/services/LockService';
import store from '../index';
import { FieldModel } from '@/types/models/workflowJob/dynamicForm/FieldModel';

export const actions: ActionTree<WorkflowJobState, RootState> = {
  setIsLoading({ commit }, isLoading: boolean) {
    commit(WorkflowJobMutationTypes.SetIsLoading, isLoading);
  },
  setIsDirty({ commit }, isDirty: boolean) {
    store.dispatch('applicationDirtyStore/setApplicationDirty', isDirty);
    commit(WorkflowJobMutationTypes.SetIsDirty, isDirty);
  },
  setIsAwaitingProcessing({ commit }, isAwaitingProcessing: boolean) {
    commit(WorkflowJobMutationTypes.SetIsAwaitingProcessing, isAwaitingProcessing);
  },
  getWorkflowJob({ commit, getters }, searchModel: WorkflowJobSearchModel) {
    commit(WorkflowJobMutationTypes.SetIsLoading, true);
    store.dispatch('documentViewStore/setDocumentSearchModel', null);
    store.dispatch('documentViewStore/loadPageWithJobId', {
      jobId: searchModel.jobId,
      page: 1
    } as WorkflowDocumentSearchModel);

    commit(WorkflowJobMutationTypes.SetIsWorkflowJobLocked, searchModel.isReadonly);
    WorkflowJobService.getWorkflowJobAsync(searchModel.queueId, searchModel.jobId, searchModel.isReadonly).then(
      (response: WorkflowJobModel) => {
        store.dispatch('documentViewStore/setDocumentSearchModel', response.documentSearchModel);
        const payload: WorkflowJobModel = response;
        commit(WorkflowJobMutationTypes.WorkflowJobLoaded, payload);

        const horizontalTabs = getters.allJobTabsByOrientation(JobTabOrientation.Horizontal) as WorkflowJobTabModel[] | undefined;

        if (horizontalTabs && horizontalTabs.length > 0) {
          commit(WorkflowJobMutationTypes.PopulateHorizontalJobTabStates, horizontalTabs);
        }

        const verticalTabs = getters.allJobTabsByOrientation(JobTabOrientation.Vertical) as WorkflowJobTabModel[] | undefined;

        if (verticalTabs && verticalTabs.length > 0) {
          commit(WorkflowJobMutationTypes.PopulateVerticalJobTabStates, verticalTabs);
        }

        const workflowTabs = getters.workflowJobTabs as WorkflowJobTabModel[] | undefined;

        if (workflowTabs && workflowTabs.length > 0) {
          workflowTabs.forEach((workflowTab: WorkflowJobTabModel) => {
            commit(WorkflowJobMutationTypes.SetDynamicFormValidationModel, {
              formId: workflowTab.dynamicFormModel.formId
            } as DynamicFormValidationModel);
          });
        }
      },
      (error) => {
        commit(WorkflowJobMutationTypes.WorkflowJobError, error);
      }
    );
  },
  setWorkflowDataSourceField(this: any, { commit, dispatch, getters }, fieldUpdateModel: FieldUpdateModel) {
    const field = getters.workflowDataSourceField(fieldUpdateModel.dataSourceKey, fieldUpdateModel.fieldName) as object | undefined;

    if (field !== undefined) {
      commit(WorkflowJobMutationTypes.UpdateWorkflowDataSourceField, fieldUpdateModel);
      if (!fieldUpdateModel.ignoreAppDirty) {
        dispatch('setIsDirty', true);
      }
    } else {
      let errorMessage = 'Unable to find workflow data source field. ';
      errorMessage += `Data source key: ${fieldUpdateModel.dataSourceKey} | `;
      errorMessage += `Field name: ${fieldUpdateModel.fieldName}`;
      commit(WorkflowJobMutationTypes.SetErrorMessage, errorMessage);
    }
  },
  setActiveHorizontalTab({ commit, getters, state }, tabKey: string) {
    const tab =
      state.activeHorizontalTab.key === tabKey && !getters.workflowDocumentIsPlaceholder ? ({} as WorkflowJobTabModel) : (getters.horizontalJobTabByKey(tabKey) as WorkflowJobTabModel | undefined);

    commit(WorkflowJobMutationTypes.SetActiveHorizontalTab, tab);
  },
  setActiveVerticalTab({ commit, getters, state }, tabKey: string) {
    const tab = getters.verticalJobTabByKey(tabKey) as WorkflowJobTabModel | undefined;
    if (tab) {
      commit(WorkflowJobMutationTypes.SetActiveVerticalTab, getActiveTab(state.activeVerticalTab, tab));
    } else {
      const tabs = getters.visibleVerticalJobTabs as WorkflowJobTabModel[];
      if (tabs) {
        commit(WorkflowJobMutationTypes.SetActiveVerticalTab, tabs[0]);
      }
    }
  },
  setJobTabStateCount({ commit, getters }, tabStateModel: TabStateModel) {
    const tab = getters.jobTabState(tabStateModel.key) as JobTabStateModel | undefined;
    if (tab) {
      commit(WorkflowJobMutationTypes.SetJobTabStateCount, tabStateModel);
    }
  },
  setJobTabStateDirty({ commit, getters }, tabStateModel: TabStateModel) {
    const tab = getters.jobTabState(tabStateModel.key) as JobTabStateModel | undefined;
    if (tab) {
      commit(WorkflowJobMutationTypes.SetJobTabStateDirty, tabStateModel);
    }
  },
  setJobTabStateHidden({ commit, getters }, tabStateModel: TabStateModel) {
    const tab = getters.jobTabState(tabStateModel.key) as JobTabStateModel | undefined;
    if (tab) {
      commit(WorkflowJobMutationTypes.SetJobTabStateHidden, tabStateModel);
    }
  },
  setExtendingLock({ commit }, payload: boolean) {
    commit(WorkflowJobMutationTypes.SetExtendingWorkflowJobLock, payload);
  },
  extendLock({ commit, getters }) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      commit(WorkflowJobMutationTypes.SetExtendingWorkflowJobLock, true);
      LockService.extendLockAsync(currentLock.id)
        .then((response: LockModel) => {
          const payload: LockModel = response;
          commit(WorkflowJobMutationTypes.ExtendWorkflowJobLock, payload);
          commit(WorkflowJobMutationTypes.SetExtendingWorkflowJobLock, false);
        })
        .catch((error: any) => {
          commit(WorkflowJobMutationTypes.ExpireWorkflowJobLock, error);
          commit(WorkflowJobMutationTypes.SetExtendingWorkflowJobLock, false);
        });
    }
  },
  releaseWorkflowJobLock({ commit, getters }, keepReleaseLockCallAlive = false) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      LockService.releaseLockAsync(currentLock.id, keepReleaseLockCallAlive).then(() => {
        commit(WorkflowJobMutationTypes.ExpireWorkflowJobLock);
      });
    }
  },
  setComponentName({ commit }, componentName: string) {
    commit(WorkflowJobMutationTypes.SetComponentName, componentName);
  },
  setErrorModel({ commit }, errorModel: ErrorModel) {
    commit(WorkflowJobMutationTypes.SetErrorModel, errorModel);
  },
  setDynamicFormValidationModel({ commit }, validationModel: DynamicFormValidationModel) {
    commit(WorkflowJobMutationTypes.SetDynamicFormValidationModel, validationModel);
  },
  addCustomFormsDynamicFormValidationModel({ commit }, formId: string) {
    commit(WorkflowJobMutationTypes.SetDynamicFormValidationModel, { formId } as DynamicFormValidationModel);
  },
  unloadWorkflowJob({ commit }) {
    commit(WorkflowJobMutationTypes.UnloadWorkflowJob);
  },
  addDynamicFormModelToCustomJobTab({ commit }, data: CustomTabDynamicFormUpdateModel) {
    commit(WorkflowJobMutationTypes.AddDynamicFormModelToCustomJobTab, data);
  },
  invalidateAllDynamicForms({ commit }) {
    commit(WorkflowJobMutationTypes.InvalidateAllDynamicForms);
  },
  setDynamicFields(this: any, { commit }, fieldModel: FieldModel) {
    commit(WorkflowJobMutationTypes.SetDynamicFields, fieldModel);
  },
  evaluateFieldRules({ getters, state, dispatch }, field: FieldUpdateModel) {
    const rules = state.workflowJob.workflowQueueConfiguration.fieldUpdateRules;

    const allRuleCriteriaFieldsSatisfied = (rule: WorkflowFieldUpdateRuleModel): boolean => {
      return rule?.criteriaFields.every((criteriaRuleField: WorkflowRuleFieldModel) => {
        if (criteriaRuleField == null) {
          return false;
        }
        const jobField = getters.workflowDataSourceField(criteriaRuleField.dataSourceKey, criteriaRuleField.dataFieldName);
        if (jobField == null) {
          return false;
        }
        return (jobField === '' && criteriaRuleField.value === '') || jobField.toLowerCase() === criteriaRuleField.value.toLowerCase();
      });
    };
    const firstMatchingRule = rules.find(
      (rule: WorkflowFieldUpdateRuleModel) =>
        rule.criteriaFields.some(
          (c: WorkflowRuleFieldModel) => c.dataSourceKey.toLowerCase() === field.dataSourceKey.toLowerCase() && c.dataFieldName.toLowerCase() === field.fieldName.toLowerCase()
        ) && allRuleCriteriaFieldsSatisfied(rule)
    );

    if (!firstMatchingRule) {
      return;
    }

    const failedUpdateDataFields: string[] = [];
    firstMatchingRule.updateFields.forEach((updateField: WorkflowRuleFieldModel) => {
      if (!getters.canUpdateDataFields(updateField)) {
        failedUpdateDataFields.push(updateField.dataFieldName);
      } else {
        const fieldUpdateModel = {
          dataSourceKey: updateField.dataSourceKey,
          fieldName: updateField.dataFieldName,
          value: updateField.value
        } as FieldUpdateModel;
        dispatch('setWorkflowDataSourceField', fieldUpdateModel);
      }
    });
    if (failedUpdateDataFields.length > 0) {
      throw new Error(
        `Changing this field caused a Field Update Rule to attempt the update of one or more read-only fields
        , which is not allowed. The read-only fields were not updated`
      );
    }
  },
  setPostProcessNavigation({ commit }, data): void {
    commit(WorkflowJobMutationTypes.SetPostProcessNavigation, data);
  },
  [StoreConstants.WorkflowJob.Actions.SetIsTabProcessing]({ commit }, isTabProcessing: boolean) {
    commit(WorkflowJobMutationTypes.SetIsTabProcessing, isTabProcessing);
  },
  [StoreConstants.WorkflowJob.Actions.SetHasWorkflowJobBeenNavigatedAwayFrom]({ commit }, hasWorkflowJobBeenNavigatedAwayFrom: boolean) {
    commit(WorkflowJobMutationTypes.SetHasWorkflowJobBeenNavigatedAwayFrom, hasWorkflowJobBeenNavigatedAwayFrom);
  },
  [StoreConstants.WorkflowJob.Actions.SetIsWorkflowJobProcessed]({ commit }, isWorkflowJobProcessed: boolean) {
    commit(WorkflowJobMutationTypes.SetIsWorkflowJobProcessed, isWorkflowJobProcessed);
  }
};

function getActiveTab(currentActiveTab: WorkflowJobTabModel, targetActiveTab: WorkflowJobTabModel): WorkflowJobTabModel {
  if (currentActiveTab.key === targetActiveTab.key) {
    return currentActiveTab;
  } else {
    return targetActiveTab;
  }
}
